/**
 * @fileoverview System report schedule service — local-first CRUD for system-scoped report schedules.
 * @module services/systemReportScheduleService
 *
 * Responsibilities:
 * - CRUD operations for system report schedules in Dexie.
 * - Toggle active/inactive state.
 * - Audit logging for all mutations.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - packages/audit (runAuditedMutation)
 * - ./systemAuditLogService (writeAuditLog)
 *
 * Architecture:
 * - Local-first: Dexie is source of truth; background sync reconciles with MongoDB.
 *
 * @localFirst
 * Source of Truth: local (Dexie)
 * Write Flow: Dexie write → wakeLocalWrite → sync push to MongoDB
 * Sync Strategy: Optimistic write; background push
 * Conflict Resolution: Last-write-wins (server timestamp)
 *
 * Notes:
 * - toggleScheduleActive delegates to updateReportSchedule and is audited through that path.
 */

import { getSystemDb, peekSystemDb, getSystemDexieRaw, wakeLocalWrite, watchDexieTable } from '@firesquirrel/local-db';
import type { ReportScheduleLocal } from '@firesquirrel/local-db';
import { runAuditedMutation } from '../../../packages/audit/src';
import { writeAuditLog } from './systemAuditLogService';

export interface ListReportSchedulesParams {
  reportDefinitionId?: string;
  isActive?: boolean;
  tenantId?: string;
}

function applyScheduleFilters(
  items: ReportScheduleLocal[],
  params?: ListReportSchedulesParams,
): ReportScheduleLocal[] {
  let results = items;

  if (params?.reportDefinitionId) {
    results = results.filter((item) => item.reportDefinitionId === params.reportDefinitionId);
  }

  if (params?.isActive !== undefined) {
    results = results.filter((item) => item.isActive === params.isActive);
  }

  return results.sort((a, b) => {
    const aDate = new Date(a.updatedAt || a.createdAt).getTime();
    const bDate = new Date(b.updatedAt || b.createdAt).getTime();
    return bDate - aDate;
  });
}

export async function listReportSchedules(
  params?: ListReportSchedulesParams,
): Promise<ReportScheduleLocal[]> {
  const db = await getSystemDb();
  const results = await db.report_schedules.toArray();
  return applyScheduleFilters(results as ReportScheduleLocal[], params);
}

/**
 * @process watchReportSchedules
 * Steps:   1. Resolve `peekSystemDb() ?? getSystemDb()` so the system DB is
 *          open and `getSystemDexieRaw()` is non-null.
 *          2. Subscribe a `liveQuery` whose FIRST call is the raw
 *          `report_schedules` table read — no non-Dexie await precedes it —
 *          so Dexie registers the object store as a tracked dependency.
 *          3. Emit filtered snapshots on every change until `unsubscribe()`.
 * Failure Handling: a still-closed raw handle emits `[]` and defers to the
 *          next emission; a failed open is forwarded to `handlers.error`.
 */
export function watchReportSchedules(
  params: ListReportSchedulesParams | undefined,
  handlers: { next: (items: ReportScheduleLocal[]) => void; error?: (err: unknown) => void },
): { unsubscribe(): void; ready: Promise<void> } {
  return watchDexieTable(
    {
      open: () => (peekSystemDb() ? Promise.resolve() : getSystemDb()),
      getRaw: getSystemDexieRaw,
      table: 'report_schedules',
      read: async (table) =>
        applyScheduleFilters((await table.toArray()) as ReportScheduleLocal[], params),
      emptyValue: [],
    },
    handlers,
  );
}

export async function getReportSchedule(id: string): Promise<ReportScheduleLocal | null> {
  const db = await getSystemDb();
  const doc = await db.report_schedules.get(id);
  return (doc as ReportScheduleLocal | undefined) ?? null;
}

/** @process createReportSchedule — Dexie mutation + audit entry. */
export async function createReportSchedule(
  data: Partial<ReportScheduleLocal>,
): Promise<ReportScheduleLocal> {
  const db = await getSystemDb();
  const now = new Date().toISOString();
  const newId = data._id || `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return runAuditedMutation<null, ReportScheduleLocal>({
    collection: 'reportSchedules',
    documentId: newId,
    action: 'create',
    summary: `Created system report schedule: ${(data as any).name ?? newId}`,
    getBefore: async () => null,
    execute: async () => {
      const newDoc = {
        ...data,
        _id: newId,
        createdAt: now,
        updatedAt: now,
      };
      await db.report_schedules.put(newDoc as any);
      wakeLocalWrite('report_schedules');
      return newDoc as ReportScheduleLocal;
    },
    getAfter: async () => (await db.report_schedules.get(newId)) as ReportScheduleLocal | null,
    writeAuditLog,
  });
}

/** @process updateReportSchedule — Dexie mutation + audit entry. */
export async function updateReportSchedule(
  id: string,
  patch: Partial<ReportScheduleLocal>,
): Promise<ReportScheduleLocal | null> {
  const db = await getSystemDb();
  const existing = await db.report_schedules.get(id);
  if (!existing) return null;

  return runAuditedMutation<ReportScheduleLocal, ReportScheduleLocal>({
    collection: 'reportSchedules',
    documentId: id,
    action: 'update',
    summary: `Updated system report schedule: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.report_schedules.get(id)) as ReportScheduleLocal | null,
    execute: async () => {
      const updated = { ...(existing as any), ...patch, updatedAt: new Date().toISOString() };
      await db.report_schedules.put(updated);
      wakeLocalWrite('report_schedules');
      return updated as ReportScheduleLocal;
    },
    getAfter: async () => (await db.report_schedules.get(id)) as ReportScheduleLocal | null,
    writeAuditLog,
  });
}

/** @process deleteReportSchedule — Dexie mutation + audit entry. */
export async function deleteReportSchedule(id: string): Promise<boolean> {
  const db = await getSystemDb();
  const existing = await db.report_schedules.get(id);
  if (!existing) return false;

  await runAuditedMutation<ReportScheduleLocal, ReportScheduleLocal>({
    collection: 'reportSchedules',
    documentId: id,
    action: 'delete',
    summary: `Deleted system report schedule: ${(existing as any).name ?? id}`,
    getBefore: async () => (await db.report_schedules.get(id)) as ReportScheduleLocal | null,
    execute: async () => {
      const now = new Date().toISOString();
      const tombstone = { ...(existing as any), _deleted: true, deletedAt: now, updatedAt: now };
      await db.report_schedules.put(tombstone);
      wakeLocalWrite('report_schedules');
      return tombstone as ReportScheduleLocal;
    },
    getAfter: async () => (await db.report_schedules.get(id)) as ReportScheduleLocal | null,
    writeAuditLog,
  });

  return true;
}

/** @process toggleScheduleActive — delegates to audited mutation. */
export async function toggleScheduleActive(id: string, isActive: boolean): Promise<ReportScheduleLocal | null> {
  return updateReportSchedule(id, { isActive });
}
